import { useCodeerApi } from "../useCodeerApi"
import { FetchMethod } from "../useFetch"
import { Response } from "@/types"
import {
  DailyOrganizationMetricsData,
  DailyUserMetricsData,
  OrganizationMetricsData,
  OrganizationStatsData,
  UserMetricsData,
  WorkspaceDashboardData,
  DashboardChartPoint,
  DashboardSeries,
} from "@/types/metrics"

// Base metrics interface with common fields (API response - snake_case)
interface BaseMetrics {
  date: string
  pdf_pages_parsed: number
  conversations_submitted: number
}

//----------------------------------------------
// ORGANIZATION METRICS
//----------------------------------------------

// Organization metrics from API
export interface DailyOrganizationMetrics extends BaseMetrics {}

export interface OrganizationStats {
  context_object_count: number
  context_size: number
  user_count: number
  connection_count: number
  workspace_count: number
}

export interface OrganizationMetricsResponse {
  metrics: DailyOrganizationMetrics[]
  status: OrganizationStats
}

// Organization transformer functions
export const transformDailyOrganizationMetrics = (
  metrics: DailyOrganizationMetrics
): DailyOrganizationMetricsData => {
  return {
    date: metrics.date,
    conversationsSubmitted: metrics.conversations_submitted,
  }
}

export const transformOrganizationStats = (
  stats: OrganizationStats
): OrganizationStatsData => {
  return {
    contextObjectCount: stats.context_object_count,
    contextSize: stats.context_size,
    userCount: stats.user_count,
    connectionCount: stats.connection_count,
    workspaceCount: stats.workspace_count,
  }
}

export const transformOrganizationMetricsData = (
  data: OrganizationMetricsResponse
): OrganizationMetricsData => {
  return {
    metrics: data.metrics.map(transformDailyOrganizationMetrics),
    status: transformOrganizationStats(data.status),
  }
}

// Organization API hook
export const useGetOrganizationMetricsApi = (organizationId: string) => {
  return useCodeerApi<
    Response<OrganizationMetricsResponse>,
    MetricsQueryParams
  >(`/metrics/organization/${organizationId}`, FetchMethod.Get)
}

//----------------------------------------------
// USER METRICS
//----------------------------------------------

// User metrics from API
export interface DailyUserMetricsResponse extends BaseMetrics {
  user_email: string
  organization_name: string
}

// User metrics transformers
export const transformDailyUserMetrics = (
  metrics: DailyUserMetricsResponse
): DailyUserMetricsData => {
  return {
    date: metrics.date,
    conversationsSubmitted: metrics.conversations_submitted,
    userEmail: metrics.user_email,
    organizationName: metrics.organization_name,
  }
}

export const transformUserMetricsData = (
  data: DailyUserMetricsResponse[]
): UserMetricsData => {
  return {
    metrics: data.map(transformDailyUserMetrics),
  }
}

// User API hook
export const useGetUserMetricsApi = (userId: number) => {
  return useCodeerApi<Response<DailyUserMetricsResponse[]>, MetricsQueryParams>(
    `/metrics/user/${userId}`,
    FetchMethod.Get
  )
}

//----------------------------------------------
// WORKSPACE DASHBOARD
//----------------------------------------------

export interface DashboardChartPointResponse {
  date: string
  value: number
}

export interface WorkspaceDashboardChartsResponse {
  daily_unique_client_users: DashboardChartPointResponse[]
  daily_histories_created: DashboardChartPointResponse[]
  daily_form_tool_calls_by_agent: DashboardSeriesResponse[]
  daily_form_submissions_by_agent: DashboardSeriesResponse[]
}

export interface DashboardSeriesPointResponse {
  date: string
  value: number
}

export interface DashboardSeriesResponse {
  id: string
  name: string
  points: DashboardSeriesPointResponse[]
}

export interface WorkspaceDashboardCardsResponse {
  unique_client_users: number
  histories_created: number
  client_conversations: number
  avg_conversations_per_user: number
  avg_conversations_per_history: number
  feedback_conversations?: number
  form_tool_calls: number
  form_submissions: number
}

export interface WorkspaceDashboardResponse {
  cards: WorkspaceDashboardCardsResponse
  charts: WorkspaceDashboardChartsResponse
  range_start: string
  range_end: string
  range_days: number
  has_data: boolean
}

export interface WorkspaceDashboardQueryParams {
  range?: number
}

const transformChartPoint = (
  point: DashboardChartPointResponse
): DashboardChartPoint => ({
  date: point.date,
  value: point.value,
})

const transformSeriesPoint = (
  point: DashboardSeriesPointResponse
): DashboardChartPoint => ({
  date: point.date,
  value: point.value,
})

const transformSeries = (series: DashboardSeriesResponse): DashboardSeries => ({
  id: series.id,
  name: series.name,
  points: series.points.map(transformSeriesPoint),
})

export const transformWorkspaceDashboardData = (
  data: WorkspaceDashboardResponse
): WorkspaceDashboardData => ({
  cards: {
    uniqueClientUsers: data.cards.unique_client_users,
    historiesCreated: data.cards.histories_created,
    clientConversations: data.cards.client_conversations,
    avgConversationsPerUser: data.cards.avg_conversations_per_user,
    avgConversationsPerHistory: data.cards.avg_conversations_per_history,
    feedbackConversations: data.cards.feedback_conversations ?? 0,
    formToolCalls: data.cards.form_tool_calls,
    formSubmissions: data.cards.form_submissions,
  },
  charts: {
    dailyUniqueClientUsers:
      data.charts.daily_unique_client_users.map(transformChartPoint),
    dailyHistoriesCreated:
      data.charts.daily_histories_created.map(transformChartPoint),
    dailyFormToolCallsByAgent:
      data.charts.daily_form_tool_calls_by_agent.map(transformSeries),
    dailyFormSubmissionsByAgent:
      data.charts.daily_form_submissions_by_agent.map(transformSeries),
  },
  rangeStart: data.range_start,
  rangeEnd: data.range_end,
  rangeDays: data.range_days,
  hasData: data.has_data,
})

export const useGetWorkspaceDashboardMetricsApi = (workspaceId: string) => {
  return useCodeerApi<
    Response<WorkspaceDashboardResponse>,
    WorkspaceDashboardQueryParams
  >(`/metrics/workspace/${workspaceId}/dashboard`, FetchMethod.Get)
}

//----------------------------------------------
// COMMON TYPES
//----------------------------------------------

export interface MetricsQueryParams {
  start_date?: string
  end_date?: string
  with_status?: boolean
}
